// T7 (LAUNCH_PLAN.md) — client-side email verification helpers.
//
// Provides:
//   - sendEmailVerificationLink(user): wraps Firebase Auth's
//     sendEmailVerification with a continueUrl that lands back at
//     /dashboard?verified=1 so we can surface a brief toast on return.
//   - refreshVerifiedClaim(user): reload + force-refresh the ID token so
//     callable / rule checks see request.auth.token.email_verified=true
//     after the user clicks the email link.
//   - isEmailNotVerifiedError(err): recognizer for callable rejections so
//     UI can render the friendly inline message.
//   - useEmailVerificationCooldown(seconds): hook that tracks a one-off
//     cooldown timer for the "Resend" button (default 60s).

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  sendEmailVerification,
  reload,
  type User as FirebaseUser,
} from 'firebase/auth';

export const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Sends the verification email with our preferred continueUrl. Throws on
 * Firebase Auth rate-limit / invalid-config errors so the caller can show
 * a retry button.
 */
export async function sendEmailVerificationLink(user: FirebaseUser): Promise<void> {
  // continueUrl must be in the Firebase Auth's "authorized domains" allow-list,
  // configured in the Firebase Console. window.location.origin works for both
  // localhost dev and the prod domain since both are registered there.
  const continueUrl = `${window.location.origin}/dashboard?verified=1`;
  await sendEmailVerification(user, { url: continueUrl });
}

/**
 * Forces an auth state refresh so the new emailVerified flag propagates.
 * Reload pulls the latest user record from Firebase Auth, then we force
 * a token refresh so subsequent callable / rule checks see the new claim.
 */
export async function refreshVerifiedClaim(user: FirebaseUser): Promise<boolean> {
  await reload(user);
  // After reload, user.emailVerified is current. If true, force-refresh the
  // ID token so the server sees email_verified=true on the next request
  // (token claims don't auto-update mid-session without this).
  if (user.emailVerified) {
    await user.getIdToken(true);
    return true;
  }
  return false;
}

interface CallableErrorShape {
  code?: string;
  details?: { reason?: string };
}

/**
 * Returns true when the caught error matches the server's email-verification
 * rejection (HttpsError code='failed-precondition' details.reason='email_not_verified').
 */
export function isEmailNotVerifiedError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const e = err as CallableErrorShape;
  return (
    e.code === 'functions/failed-precondition' &&
    e.details?.reason === 'email_not_verified'
  );
}

/**
 * Cooldown hook for the "Resend email" button. Returns the remaining
 * seconds (0 when ready) and a `start()` callback. The cooldown survives
 * component re-mounts via a ref so the user can't trivially bypass it by
 * dismissing the banner.
 */
export function useEmailVerificationCooldown(seconds = RESEND_COOLDOWN_SECONDS): {
  remaining: number;
  start: () => void;
} {
  const [remaining, setRemaining] = useState<number>(0);
  const endRef = useRef<number>(0);

  useEffect(() => {
    if (remaining <= 0) return;
    const id = window.setInterval(() => {
      const left = Math.max(0, Math.ceil((endRef.current - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0) window.clearInterval(id);
    }, 250);
    return () => window.clearInterval(id);
  }, [remaining]);

  const start = useCallback(() => {
    endRef.current = Date.now() + seconds * 1000;
    setRemaining(seconds);
  }, [seconds]);

  return { remaining, start };
}
